import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Info, X } from 'lucide-react'
import { useSiteNotice } from '../../hooks/shared/useSiteNotice'
import { useMaintenanceStore } from '../../store/maintenanceStore'
import { dismissSiteNotice, isSiteNoticeDismissed } from '../../lib/siteNoticeDismiss'
import { resolveSiteNoticeMessage } from '../../lib/siteNoticeMessage'
import type { SiteNoticeAudience, SiteNoticeSeverity } from '../../types/siteNotice'

const SEVERITY_STYLES: Record<
  SiteNoticeSeverity,
  { container: string; icon: string; dismiss: string }
> = {
  info: {
    container: 'bg-blue-50 border-b border-blue-200 text-blue-900',
    icon: 'text-blue-500',
    dismiss: 'text-blue-500 hover:text-blue-700',
  },
  warning: {
    container: 'bg-amber-50 border-b border-amber-200 text-amber-900',
    icon: 'text-amber-500',
    dismiss: 'text-amber-500 hover:text-amber-700',
  },
  critical: {
    container: 'bg-red-50 border-b border-red-200 text-red-900',
    icon: 'text-red-500',
    dismiss: 'text-red-500 hover:text-red-700',
  },
}

interface Props {
  context: SiteNoticeAudience
}

/**
 * Admin-managed advance notice banner (planned maintenance / outage).
 * Distinct from MaintenanceBanner (live nginx 503 during deploy).
 */
export default function SiteNoticeBanner({ context }: Props) {
  const { i18n, t } = useTranslation('common')
  const { data } = useSiteNotice()
  const isMaintenanceActive = useMaintenanceStore((s) => s.isActive)
  const [dismissedLocally, setDismissedLocally] = useState(false)

  const notice = data?.notice
  if (!notice || isMaintenanceActive) return null

  const visibleForContext =
    context === 'authenticated'
      ? notice.show_on_authenticated
      : notice.show_on_public
  if (!visibleForContext) return null

  const message = resolveSiteNoticeMessage(notice.messages, i18n.language)
  if (!message) return null

  const isDismissed =
    dismissedLocally ||
    (notice.dismissible && isSiteNoticeDismissed(notice.id))
  if (isDismissed) return null

  const styles = SEVERITY_STYLES[notice.severity]
  const Icon = notice.severity === 'info' ? Info : AlertTriangle

  const handleDismiss = () => {
    dismissSiteNotice(notice.id)
    setDismissedLocally(true)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-full px-4 py-2.5 flex items-center justify-between gap-4 text-sm ${styles.container}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`h-4 w-4 flex-shrink-0 ${styles.icon}`} aria-hidden="true" />
        <span className="min-w-0">{message}</span>
      </div>
      {notice.dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          className={`p-0.5 flex-shrink-0 ${styles.dismiss}`}
          aria-label={t('siteNotice.dismiss')}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
